/**
 * Lightweight document.title + meta description manager.
 *
 * Avoids pulling in react-helmet-async — a hook on each page is
 * enough for client-side SEO. Real crawler-friendly SEO needs SSR
 * (out of scope today; see docs/SETUP_REQUIRED.md).
 */

import { useEffect } from 'react';

const DEFAULT_TITLE = 'GammaVision — Real-time Options Gamma & Vanna Heatmap';

function ensureMeta(name: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  return el;
}

function ensureProperty(prop: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', prop);
    document.head.appendChild(el);
  }
  return el;
}

export function usePageMeta(title: string, description?: string) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const desc = description ?? '';
    const descMeta = ensureMeta('description');
    const previousDesc = descMeta.content;
    descMeta.content = desc;

    const ogTitle = ensureProperty('og:title');
    const previousOgTitle = ogTitle.content;
    ogTitle.content = title;

    const ogDesc = ensureProperty('og:description');
    const previousOgDesc = ogDesc.content;
    ogDesc.content = desc;

    const twTitle = ensureMeta('twitter:title');
    const previousTwTitle = twTitle.content;
    twTitle.content = title;

    const twDesc = ensureMeta('twitter:description');
    const previousTwDesc = twDesc.content;
    twDesc.content = desc;

    return () => {
      document.title = previousTitle || DEFAULT_TITLE;
      descMeta.content = previousDesc;
      ogTitle.content = previousOgTitle;
      ogDesc.content = previousOgDesc;
      twTitle.content = previousTwTitle;
      twDesc.content = previousTwDesc;
    };
  }, [title, description]);
}
